/* ImportStatusOverlay — a big, unmissable full-screen overlay for the catalog
 * import flow. Two states:
 *   • loading  → large animated spinner + reassuring rotating copy while the
 *                file is parsed (replaces the tiny bottom pill).
 *   • error    → clear reason WHY the import failed + concrete suggested steps
 *                so the seller can fix the file and retry.
 */
import { useEffect, useState } from "react";
import { Loader2, FileSpreadsheet, AlertTriangle, X, RefreshCcw, Lightbulb } from "lucide-react";
import { Button } from "../ui/button";

const LOADING_LINES = [
  "Reading your file…",
  "Detecting the column structure…",
  "Mapping columns to your catalog fields…",
  "Almost there — preparing your preview…",
];

export default function ImportStatusOverlay({ loading, filename, error, onClose, onRetry }) {
  const [line, setLine] = useState(0);

  useEffect(() => {
    if (!loading) { setLine(0); return undefined; }
    const id = setInterval(() => setLine((l) => (l + 1) % LOADING_LINES.length), 2200);
    return () => clearInterval(id);
  }, [loading]);

  if (!loading && !error) return null;

  return (
    <div
      className="fixed inset-0 z-[200] grid place-items-center px-6 bg-foreground/40 backdrop-blur-sm"
      data-testid={loading ? "import-loading-overlay" : "import-error-overlay"}
    >
      {loading ? (
        <div className="w-full max-w-md rounded-2xl bg-background border border-border shadow-2xl p-10 text-center">
          <div className="relative mx-auto mb-7 w-20 h-20">
            <span className="absolute inset-0 rounded-full bg-foreground/5 animate-ping" />
            <span className="absolute inset-0 grid place-items-center">
              <Loader2 className="w-10 h-10 animate-spin text-foreground" />
            </span>
          </div>
          <h2 className="display text-2xl tracking-tight" data-testid="import-loading-title">
            {LOADING_LINES[line]}
          </h2>
          {filename && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-secondary border border-border px-3 py-1.5 text-[12px] text-foreground/70 max-w-full">
              <FileSpreadsheet className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{filename}</span>
            </div>
          )}
          <p className="mt-5 text-[13px] text-foreground/55 leading-relaxed">
            AI is analysing your spreadsheet and matching every column. Large files can take up to a minute — hang tight.
          </p>
          <div className="mt-6 h-1.5 w-full rounded-full bg-secondary overflow-hidden">
            <div className="h-full w-1/3 rounded-full bg-foreground animate-[importbar_1.4s_ease-in-out_infinite]" />
          </div>
          <style>{`@keyframes importbar{0%{margin-left:-35%}100%{margin-left:100%}}`}</style>
        </div>
      ) : (
        <div className="w-full max-w-lg rounded-2xl bg-background border border-border shadow-2xl p-8" data-testid="import-error-card">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-rose-50 border border-rose-200 grid place-items-center shrink-0">
              <AlertTriangle className="w-6 h-6 text-rose-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="display text-xl tracking-tight" data-testid="import-error-title">{error.title}</h2>
              <p className="mt-2 text-[13.5px] text-foreground/65 leading-relaxed">{error.detail}</p>
            </div>
            <button onClick={onClose} className="text-foreground/40 hover:text-foreground p-1" data-testid="import-error-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {error.suggestions?.length > 0 && (
            <div className="mt-6 rounded-xl border border-border bg-secondary/50 p-5">
              <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-foreground/70">
                <Lightbulb className="w-4 h-4 text-amber-500" /> How to fix it
              </div>
              <ul className="mt-3 space-y-2.5" data-testid="import-error-suggestions">
                {error.suggestions.map((s, i) => (
                  <li key={i} className="flex gap-2.5 text-[13px] text-foreground/75 leading-relaxed">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-foreground/40 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error.errors?.length > 0 && (
            <details className="mt-4 group" data-testid="import-error-details">
              <summary className="cursor-pointer text-[12px] text-foreground/55 hover:text-foreground select-none">
                Show the first {Math.min(error.errors.length, 10)} problem row{error.errors.length === 1 ? "" : "s"}
              </summary>
              <div className="mt-2 max-h-40 overflow-auto rounded-lg border border-border bg-background p-3 text-[11.5px] text-foreground/60 space-y-1 mono">
                {error.errors.slice(0, 10).map((e, i) => (
                  <div key={i} className="truncate">• {typeof e === "string" ? e : JSON.stringify(e)}</div>
                ))}
              </div>
            </details>
          )}

          <div className="mt-7 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-full h-10 px-5">Close</Button>
            {onRetry && (
              <Button onClick={onRetry} className="rounded-full h-10 px-5 bg-foreground text-background hover:bg-foreground/90" data-testid="import-error-retry">
                <RefreshCcw className="w-4 h-4 mr-1.5" /> Choose another file
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
